import clsx from 'clsx';
import { CircleMinus, Plus, Trash2 } from 'lucide-react';
import type { TSlot } from '@hubertai/screening-question-types';
import MutedText from 'components/common/MutedText';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertInput from 'components/HubertUI/HubertInput';
import HubertSelect from 'components/HubertUI/HubertSelect';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import HubertTextArea from 'components/HubertUI/HubertTextArea';
import { useQuestionEditorContext } from 'components/Questionsv2/builder/context/create-question.context';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionLanguage } from 'rtk/questionEditorSelector';
import { addSlotOption, removeSlot, removeSlotOption, updateSlot, updateSlotOption } from 'rtk/Question.Editor.Slice';
import FieldLabel from './FieldLabel';

const DATATYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'enum', label: 'Options (enum)' },
];

const ID_INFO = (
  <>
    Sent to the model as the <strong>extraction field name</strong>, and used as the <strong>storage key</strong> on the
    saved answer. A short, meaningful snake_case name (e.g. <em>date_of_birth</em>) improves extraction quality and
    keeps stored answers readable. Avoid changing it once the question is live — answers already saved reference the old
    id.
  </>
);

const HINT_INFO = (
  <>
    Model-only guidance for this one field — never shown to the candidate. Write it in English. Use it to define what
    counts as a valid value, e.g.{' '}
    <em>&quot;ISO YYYY-MM-DD; only fill when day, month and year are all present&quot;</em>. Telling the model to leave
    the field empty unless the answer is substantive is what makes it re-ask.
  </>
);

const REASK_INFO = (
  <>
    Shown to the candidate when this field is still missing. Leave empty to let the model phrase the follow-up itself in
    the candidate&apos;s language; fill it in for full control over the wording.
  </>
);

interface Props {
  slot: TSlot;
  slotIndex: number;
}

const Slot = ({ slot, slotIndex }: Props) => {
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const language = useAppSelector((state) => selectQuestionLanguage(state, questionId));

  const patch = (value: Partial<TSlot>) => dispatch(updateSlot({ questionId, index: slotIndex, patch: value }));
  const title = slot.label[language]?.trim() || slot.id.trim();

  return (
    <div className="overflow-hidden rounded-md border">
      <div className={clsx('flex flex-row items-center justify-between gap-3', 'border-b bg-gray-50', 'px-3 py-2')}>
        <div className="flex min-w-0 flex-row items-center gap-2">
          <span className="rounded bg-gray-200 px-1.5 py-0.5 text-xs font-medium text-gray-600">{slotIndex + 1}</span>
          <span className="truncate text-sm font-medium">{title || <MutedText text="Untitled slot" />}</span>
        </div>

        <div className="flex shrink-0 flex-row items-center gap-3">
          <div className="flex flex-row items-center gap-2">
            <MutedText className="text-xs" text="Required" />
            <HubertSwitch checked={slot.required} onCheckedChange={(value) => patch({ required: value })} />
          </div>
          <Trash2
            size={16}
            className="cursor-pointer text-gray-400 hover:text-gray-700"
            onClick={() => dispatch(removeSlot({ questionId, index: slotIndex }))}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 p-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex flex-col gap-1">
            <FieldLabel label="Field id" info={ID_INFO} />
            <HubertInput placeholder="date_of_birth" value={slot.id} onChange={(e) => patch({ id: e.target.value })} />
          </div>

          <div className="flex flex-col gap-1">
            <FieldLabel label="Label" hint="for reports" />
            <HubertInput
              placeholder="Date of birth"
              value={slot.label[language] ?? ''}
              onChange={(e) => patch({ label: { ...slot.label, [language]: e.target.value } })}
            />
          </div>

          <div className="flex flex-col gap-1">
            <FieldLabel label="Type" />
            <HubertSelect
              list={DATATYPES}
              valuePlaceholder="Type"
              value={slot.datatype}
              onValueChange={(value) => patch({ datatype: value as TSlot['datatype'] })}
            />
          </div>
        </div>

        {slot.datatype === 'enum' && (
          <div className="flex flex-col gap-2 rounded-md bg-gray-50 p-3">
            <FieldLabel label="Allowed values" hint="the model must pick one of these" />
            {(slot.options ?? []).length === 0 ? (
              <MutedText className="text-xs" text="No values yet." />
            ) : (
              (slot.options ?? []).map((option, optionIndex) => (
                <div key={`opt-${optionIndex}`} className="flex flex-row items-center gap-2">
                  <HubertInput
                    placeholder="value (stored)"
                    value={option.value}
                    onChange={(e) =>
                      dispatch(
                        updateSlotOption({ questionId, slotIndex, optionIndex, patch: { value: e.target.value } }),
                      )
                    }
                  />
                  <HubertInput
                    placeholder="label (shown in reports)"
                    value={option.label[language] ?? ''}
                    onChange={(e) =>
                      dispatch(
                        updateSlotOption({
                          questionId,
                          slotIndex,
                          optionIndex,
                          patch: { label: { ...option.label, [language]: e.target.value } },
                        }),
                      )
                    }
                  />
                  <CircleMinus
                    size={16}
                    className="shrink-0 cursor-pointer text-gray-400 hover:text-gray-700"
                    onClick={() => dispatch(removeSlotOption({ questionId, slotIndex, optionIndex }))}
                  />
                </div>
              ))
            )}
            <HubertButton
              size="sm"
              variant="secondary"
              className="self-start"
              iconLeft={<Plus size={14} />}
              onClick={() => dispatch(addSlotOption({ questionId, slotIndex }))}
            >
              Add value
            </HubertButton>
          </div>
        )}

        {slot.datatype === 'number' && (
          <div className="flex flex-col gap-2 rounded-md bg-gray-50 p-3">
            <FieldLabel label="Valid range" hint="values outside it are discarded and re-asked" />
            <div className="flex flex-row items-center gap-2">
              <HubertInput
                type="number"
                className="w-28"
                placeholder="min"
                value={slot.constraints?.min ?? ''}
                onChange={(e) =>
                  patch({
                    constraints: {
                      ...slot.constraints,
                      min: e.target.value === '' ? undefined : Number(e.target.value),
                    },
                  })
                }
              />
              <MutedText className="text-xs" text="to" />
              <HubertInput
                type="number"
                className="w-28"
                placeholder="max"
                value={slot.constraints?.max ?? ''}
                onChange={(e) =>
                  patch({
                    constraints: {
                      ...slot.constraints,
                      max: e.target.value === '' ? undefined : Number(e.target.value),
                    },
                  })
                }
              />
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <FieldLabel label="Extraction hint" hint="model-only, English" info={HINT_INFO} />
          <HubertTextArea
            rows={2}
            placeholder="e.g. The candidate's date of birth as ISO YYYY-MM-DD. Leave empty unless day, month and year are all given."
            value={slot.extract_hint ?? ''}
            onChange={(e) => patch({ extract_hint: e.target.value })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <FieldLabel label="Re-ask if missing" hint="shown to the candidate" info={REASK_INFO} />
          <HubertTextArea
            rows={2}
            placeholder="e.g. Could you give me your full date of birth — day, month and year?"
            value={slot.reask?.[language] ?? ''}
            onChange={(e) => patch({ reask: { ...slot.reask, [language]: e.target.value } })}
          />
        </div>
      </div>
    </div>
  );
};

export default Slot;
